import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useLocation } from '@reach/router'
import { Link } from 'gatsby'
import gsap from 'gsap'
import { minorMenuLinks, menuLinks } from 'templates/layout/menu-links'
import useMenuVariation from '../desktop/use-menu-variation'

const MobileMenu = ({ expectHero, bgClass, colorClass }) => {
	const { pathname } = useLocation()
	const panelContainerRef = useRef()
	const overlayRef = useRef()
	const { current: tl } = useRef(gsap.timeline({ paused: true }))

	const shouldAutoDetect = bgClass === 'auto' && colorClass === 'auto'
	const variation = useMenuVariation(expectHero, shouldAutoDetect)

	const buildColorClass = () => {
		if (shouldAutoDetect) {
			return variation === 'light'
				? 'text-white bg-transparent'
				: 'text-black bg-white'
		}
		return colorClass + ' ' + bgClass
	}

	const [isServicesExpanded, setIsServicesExpanded] = useState(
		pathname.includes('/services/')
	)
	const serviceLinks = useMemo(() => {
		const itemWithChildren = menuLinks.filter(({ children }) => Boolean(children))
		// if (itemWithChildren.length === 0) {
		// 	throw new Error('Where is item with children?')
		// }

		return itemWithChildren[0]
	}, [])

	useEffect(() => {
		tl.to(panelContainerRef.current, {
			opacity: 0,
			xPercent: 100,
			ease: 'power2.out',
			duration: 0.1,
		}).to(
			overlayRef.current,
			{
				display: 'block',
				opacity: 1,
				x: 0,
				ease: 'power2.in',
				duration: 0.1,
			},
			'<'
		)

		return () => {
			tl.invalidate().kill()
		}
	}, [tl])

	const disableBodyScroll = () => {
		document.body.style.overflow = 'hidden'
		document.body.style['touch-action'] = 'none'
	}
	const restoreBodyScroll = () => {
		document.body.style.overflow = 'unset'
		document.body.style['touch-action'] = 'auto'
	}

	const expandMenu = () => {
		disableBodyScroll()
		tl.play()
	}
	const collapseMenu = () => {
		restoreBodyScroll()
		tl.reverse()
	}
	const linkClickHandler = () => {
		restoreBodyScroll()
	}

	const buildButtons = (isInsideMenu) => {
		const logoParts = [
			<div key="cube" className={'w-10'}>
				<svg
					viewBox="0 0 80 66"
					xmlns="http://www.w3.org/2000/svg"
					className="fill-current"
				>
					<path
						d="M32.215.347h34.674L80 46.364l-30.037 19.29H14.2L0 19.358z"
						fillRule="nonzero"
					/>
				</svg>
			</div>,
		]

		return (
			<>
				{isInsideMenu ? (
					<button
						className="absolute top-0 left-0 flex items-center px-screen-step pt-3 pb-2"
						onClick={collapseMenu}
						tabIndex="-1"
					>
						<svg
							className="h-10 w-10 stroke-current"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeWidth="1"
								fill="none"
								fillRule="evenodd"
								d="M3.964 3.999 20.036 20.07M3.964 3.999 20.036 20.07M20.036 3.999 3.964 20.07"
							/>
						</svg>
					</button>
				) : (
					<button
						onClick={expandMenu}
						className={`absolute top-0 left-0 z-10 flex items-center px-screen-step pt-3 pb-2`}
						role="menu"
						aria-hidden="true"
					>
						<svg
							className="h-10 w-10 stroke-current"
							viewBox="0 0 40 10"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g strokeWidth="2" fill="none" fillRule="evenodd">
								<path d="M0 1h40M0 9h40" />
							</g>
						</svg>
					</button>
				)}
				{isInsideMenu && pathname === '/' ? (
					<div
						className={`absolute top-0 right-0 flex h-full items-center px-[6.25vw] pt-4 pb-2`}
					>
						{logoParts}
					</div>
				) : (
					<Link
						to="/"
						onClick={isInsideMenu ? linkClickHandler : () => {}}
						className={`absolute top-0 right-0 flex h-full items-center px-[6.25vw] pt-5 pb-4`}
					>
						{logoParts}
					</Link>
				)}
			</>
		)
	}

	return (
		<>
			<div
				ref={panelContainerRef}
				className={`fixed top-0 left-0 z-10 flex h-[3.75rem] w-full items-center justify-between lg:hidden ${buildColorClass()} transition-all duration-300`}
			>
				{buildButtons(false)}
			</div>

			<div
				ref={overlayRef}
				className={`fixed top-0 left-0 z-50 h-screen w-full -translate-x-full overflow-y-scroll bg-white`}
				style={{ display: 'none', opacity: 0 }}
			>
				<div className="min-h-full w-full pb-32 text-black">
					<div className={`relative flex h-[3.75rem] w-full items-center`}>
						{buildButtons(true)}
					</div>

					<div className="px-screen-step-2 font-headings text-2xl leading-tight">
						{menuLinks.map((link) =>
							link?.children ? (
								<div
									className="border-b border-lite-gray py-8"
									key={link.title}
								>
									<button
										className="flex w-full items-center justify-between"
										onClick={() =>
											setIsServicesExpanded((prev) => !prev)
										}
									>
										{link.title}
									</button>

									<div
										className={`mt-3 ${
											isServicesExpanded ? 'visible' : 'hidden'
										} font-headings text-xl leading-tight`}
									>
										{serviceLinks.map((link) =>
											link.url === pathname ? (
												<div
													className="pt-4 text-accent"
													key={link.url}
												>
													{link.title}
												</div>
											) : (
												<Link
													className="block pt-4"
													to={link.url}
													key={link.url}
													onClick={linkClickHandler}
												>
													{link.title}
												</Link>
											)
										)}
									</div>
								</div>
							) : link.url === pathname ? (
								<div
									className="border-b border-lite-gray py-8 text-accent"
									key={link.url}
								>
									{link.title}
								</div>
							) : (
								<Link
									className="block border-b border-lite-gray py-8"
									to={link.url}
									key={link.url}
									onClick={linkClickHandler}
								>
									{link.title}
								</Link>
							)
						)}
					</div>

					<div className="mt-7 px-screen-step-2 font-headings text-xl leading-tight">
						{minorMenuLinks.map((link) =>
							link.url === pathname ? (
								<div className="pt-4 text-accent" key={link.url}>
									{link.title}
								</div>
							) : (
								<Link
									className="block pt-4"
									to={link.url}
									key={link.url}
									onClick={linkClickHandler}
								>
									{link.title}
								</Link>
							)
						)}
					</div>
				</div>
			</div>
		</>
	)
}

export default MobileMenu
